import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { RouteReuseService } from './app/core/services/route-reuse.service';
import { CustomReuseStrategy } from './app/core/services/custom-reuse.strategy';
import { appRoutes } from './app/routes-config/entry-routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

// =============================================================================
// Authentication Interceptor
// NEW: Using core module interceptor (delegated auth via auth-ui)
// OLD: ./app/services/interceptors/auth.interceptor (deprecated)
// =============================================================================
import { authInterceptor } from './app/core/interceptors/auth.interceptor';
import { publicApiSignatureInterceptor } from './app/core/interceptors/public-api-signature.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';

import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';

import { AppInitializerProvider } from './app/core/services/app-initializer.service';
import { VersionService } from './app/core/services/version.service';
import { loadRuntimeConfig } from './app/core/config/runtime-config';
import { provideSseLiveConnection } from './app/core/sse-live-connection.provider';
import { provideFormsEventManager, provideFormsGateway } from '@tuain/ng-forms';
import { EventManagerService } from './app/core/services/event-manager.service';
import { StorageService } from './app/core/services/storage.service';
import { AppLoggerService } from './app/core/services/app-logger.service';
import { defaultForms } from './app/core/services/form-manager.service.config';
import { defaultConfig } from './app/app.config';
import { NZ_I18N, es_ES as localeZorro } from 'ng-zorro-antd/i18n';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';

import { IconDefinition } from '@ant-design/icons-angular';
import {
  AlertOutline,
  ArrowLeftOutline,
  BankOutline,
  BellOutline,
  CalendarOutline,
  ClockCircleOutline,
  CloudTwoTone,
  CloudUploadOutline,
  DownloadOutline,
  EnvironmentOutline,
  FileProtectOutline,
  FileTextOutline,
  FileOutline,
  HomeOutline,
  IdcardOutline,
  LineOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  MobileOutline,
  NotificationOutline,
  NumberOutline,
  PaperClipOutline,
  PercentageOutline,
  PhoneOutline,
  ProfileOutline,
  QuestionCircleOutline,
  RiseOutline,
  SafetyCertificateOutline,
  SearchOutline,
  StarOutline,
  WalletOutline,
  WarningOutline,
  FormOutline,
  DashboardOutline,
  CustomerServiceOutline,
  FilePdfOutline,
  LinkOutline,
  MessageOutline,
  PictureOutline,
  ReadOutline,
  SendOutline,
  VideoCameraOutline,
  GlobalOutline,
  ArrowRightOutline,
  MailOutline,
  UserOutline,
  UnorderedListOutline,
  UpOutline,
} from '@ant-design/icons-angular/icons';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideTranslateService } from '@ngx-translate/core';
import { provideNgProgressOptions } from 'ngx-progressbar';
import { provideNgProgressRouter } from 'ngx-progressbar/router';
import { provideNgProgressHttp } from 'ngx-progressbar/http';
import { MarkdownModule } from 'ngx-markdown';
import { SHARED_MODULES } from './app/shared/shared-standalone';

import {
  FIELD_RENDERERS,
  TABLE_RENDERERS,
  SECTION_RENDERERS,
  SUBSECTION_RENDERERS,
  appFieldRenderers,
  appTableRenderers,
  appSectionRenderers,
  appSubsectionRenderers,
} from '@shared/components/app-renderers';

registerLocaleData(es);

if (environment.production) {
  enableProdMode();
}

const icons: IconDefinition[] = [
  AlertOutline,
  ArrowLeftOutline,
  BankOutline,
  BellOutline,
  CalendarOutline,
  ClockCircleOutline,
  CloudTwoTone,
  CloudUploadOutline,
  DownloadOutline,
  EnvironmentOutline,
  FileProtectOutline,
  FileTextOutline,
  FileOutline,
  HomeOutline,
  IdcardOutline,
  LineOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  MobileOutline,
  NotificationOutline,
  NumberOutline,
  PaperClipOutline,
  PercentageOutline,
  PhoneOutline,
  ProfileOutline,
  QuestionCircleOutline,
  RiseOutline,
  SafetyCertificateOutline,
  SearchOutline,
  StarOutline,
  WalletOutline,
  WarningOutline,
  FormOutline,
  DashboardOutline,
  CustomerServiceOutline,
  FilePdfOutline,
  LinkOutline,
  MessageOutline,
  PictureOutline,
  ReadOutline,
  // El chat usa nzType="send"; sin registrar, ng-zorro intentaba resolverlo por HTTP
  // y el botón de enviar quedaba sin ícono.
  SendOutline,
  VideoCameraOutline,
  // BUG-274936. Mismo caso que `send`: si el ícono no está aquí, ng-zorro lo pide por HTTP
  // a `assets/<tema>/<nombre>.svg`, el SPA responde el index.html y el hueco se queda en
  // blanco — sin error en consola. `global` es el de «Residente en el exterior», que salía
  // vacío en la captura de QA; los otros tres salieron de auditar los nzType del repo
  // contra lo registrado aquí y contra el juego propio de ng-zorro.
  GlobalOutline, // tarjeta «Residente en el exterior» (afiliación)
  ArrowRightOutline, // tabla de retiros parciales
  MailOutline, // certificados: envío por correo
  UserOutline, // chat IA
  UnorderedListOutline, // centro de ayuda: seleccionar otra sección (281480)
  UpOutline,
];

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    AppInitializerProvider,
    provideAppInitializer(() => loadRuntimeConfig()),
    provideAppInitializer(() => inject(VersionService).load()),
    provideSseLiveConnection(),

    /*
      Transporte de formularios (@tuain/ng-forms >= 22.4). Sustituye a la copia que esta aplicación
      tenía en api-gateway.service.ts y core/security/session-key.ts.

      `useExisting: EventManagerService` NO es opcional: esta aplicación tiene su propio gestor de
      eventos y, sin declararlo, la librería publicaría en uno distinto del que la aplicación escucha.
      Los eventos se perderían sin ningún error.

      La configuración va por fábrica porque la carga de configuración en tiempo de ejecución completa
      `defaultConfig` en un inicializador: leerla al construir el proveedor la tomaría vacía.
    */
    provideFormsEventManager({ useExisting: EventManagerService }),
    provideFormsGateway({
      config: {
        useFactory: () => ({
          serverEntryPoint: defaultConfig.serverEntryPoint,
          apiRootPath: defaultConfig.apiRootPath,
          clientAppCode: defaultConfig.clientAppCode,
          signatureKey: defaultConfig.signatureKey,
          popEnabled: true,
          connectionErrorMessage: 'Estamos teniendo problemas de conexión con los sistemas de FNA, intenta nuevamente',
          // El sondeo de sesión lo dispara la aplicación, no el usuario: contarlo como actividad
          // renovaría la sesión sola y la expiración por inactividad no llegaría nunca.
          activityExemptForms: ['activityCheck'],
        }),
      },
      storage: { useExisting: StorageService },
      logger: { useExisting: AppLoggerService },
      defaultDefinitions: { useValue: defaultForms },
    }),
    { provide: NZ_I18N, useValue: localeZorro },
    /*
      ng-zorro 22 exige un adaptador de fechas explícito: sin él, cualquier campo de fecha u hora
      falla en ejecución con NG0201 (No provider found for `NzDateAdapter`). El build no lo
      detecta y sólo una prueba lo destapó.

      Se elige el de date-fns y no el nativo porque es lo que ng-zorro usaba internamente hasta la
      21 —`date-fns` ya era dependencia declarada del proyecto—, así que el formateo de fechas no
      cambia con la migración.
    */
    provideNzDateFnsAdapter(),
    provideRouter(appRoutes), // withRouterConfig({ onSameUrlNavigation: 'ignore', // scrollPositionRestoration: 'enabled', })
    {
      provide: RouteReuseStrategy,
      useFactory: (reuseService: RouteReuseService) => new CustomReuseStrategy(reuseService),
      deps: [RouteReuseService],
    },
    provideHttpClient(withInterceptors([authInterceptor, publicApiSignatureInterceptor])),
    provideAnimations(),
    { provide: FIELD_RENDERERS, useValue: appFieldRenderers },
    { provide: TABLE_RENDERERS, useValue: appTableRenderers },
    { provide: SECTION_RENDERERS, useValue: appSectionRenderers },
    { provide: SUBSECTION_RENDERERS, useValue: appSubsectionRenderers },
    provideTranslateService(),
    // El registro de controladores de chart.js vive ahora en app-pie-table y app-chart-table,
    // que son renderers diferidos: aquí arrastraba chart.js al bundle inicial de toda la app.
    // ngx-progressbar 14 movió `thick` y `color` a variables CSS
    // (--ng-progress-thickness / --ng-progress-color); ya no son opciones.
    provideNgProgressOptions({ spinner: false }),
    provideNgProgressRouter({}),
    provideNgProgressHttp({}),
    importProvidersFrom(
      NgbModule,
      MarkdownModule.forRoot(),
      NzIconModule.forRoot(icons),
      // módulos propios
      ...SHARED_MODULES
    ),
  ],
});
